import * as React from 'react'
import { useFormikContext } from 'formik'
import { SalesTeam } from '@user/store/salesTeam/typings'
import type {
  ISalesFunnel,
  ISalesFunnelStage,
} from '@user/store/salesFunnel/typings'
import { FormSection } from '@sales-center/components'
import { OwnerSelect } from '@user/components/Owner'
import { ENTITY_TYPES } from '@sales-center/common'
import TextField, { type TextFieldProps } from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import { LeadAssignmentRuleInfoPopoverButton } from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleInfo/LeadAssignmentRuleInfoPopoverButton'

import { useData, useEffects, useFormFields } from './hooks'
import { ILeadFormModel } from '../../typings'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import FormHelperText from '@mui/material/FormHelperText'
import { leadStatusDisqualified } from '@user/components/Settings/EntityFormSettings'
import { SalesTeamSelect } from '../../FormFields/SalesTeamSelect/SalesTeamSelect'
import { SalesFunnelSelect } from '../../FormFields/SalesFunnelSelect/SalesFunnelSelect'
import { LeadStatusSelect } from '../../FormFields/LeadStatusSelect/LeadStatusSelect'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { DensityType } from '@user/components/Opportunities/AddOpportunityPage/typings'
import { useTheme } from '@mui/material/styles'

export interface Props {
  salesTeams: SalesTeam[]
  salesFunnels: ISalesFunnel[]
  salesFunnelStages: ISalesFunnelStage[]
  branchId?: string
  addFromLocation?: boolean | '' | null
  textFieldProps?: TextFieldProps
  spacing?: number
  disabled?: boolean
  hideOptional?: boolean
}

function Assignment(props: Props) {
  const {
    salesTeams,
    salesFunnels,
    salesFunnelStages,
    branchId,
    textFieldProps,
    spacing = 2,
    disabled = false,
  } = props

  const tidalThemeActive = useTidalThemeActive()

  const {
    isOverriden,
    salesOwnerShipDisabled,
    salesTeamLocked,
    assignmentFieldsLocked,
    onChangeOwnerShip,
    onChangeOwner,
    onChangeSalesFunnel,
    onChangeSalesTeam,
    positionBasedAccessText,
    selectedSalesTeamAccessText,
    rule,
    sortedOptions,
    salesTeamsFiltered,
    disqualifyReasons,
    allowManualDisqualifyReasons,
    leadStatus,
    reasonIdError,
    reasonIdErrorTxt,
    customReasonError,
    customReasonErrorTxt,
    invalidBranch,
    defaultOwnerId,
    ignoreDefault,
    disableAssignmentRule,
    canOverrideAutomatedAssignment,
  } = useData({ branchId, disabled, salesTeams, salesFunnelStages })

  const {
    reasonField,
    customReasonRef,
    customReasonField,
    onFocusCustomReason,
    customReasonHelperText,
  } = useFormFields()

  const {
    values: { salesTeamId, reasonId },
  } = useFormikContext<ILeadFormModel>()

  useEffects({ branchId, salesTeams, rule, salesTeamsFiltered })
  const density: DensityType = tidalThemeActive ? 'normal' : 'compact'
  const theme = useTheme()

  // Both fields follow ownerFieldPolicy rather than a local copy of the rule.
  //
  // They used to carry their own copy of the pre-territory rule -- lock whenever a single-owner
  // rule applies and the user cannot override -- which meant the policy reached only this
  // surface's funnel and status fields, and a rep dropping a pin inside a single-owner TERRITORY
  // still got a read-only owner. Unlocking that case is the thing ownerFieldPolicy was written
  // to do, and this is the surface the map's Add Lead flow actually uses.
  //
  // One behaviour change beyond the territory case: a conventional single-owner rule now locks
  // as soon as it applies, where the old condition also required the owner to already equal the
  // rule's one approved id. In practice that is a timing difference, since such a rule pre-fills
  // that very owner.
  const ownerDisabled = invalidBranch ? false : salesOwnerShipDisabled

  // Not the owner flag. A rule can pin the sales team without naming a single owner, and the
  // team has to stay locked in that case for anyone who cannot override the rule.
  const salesTeamDisabled = invalidBranch ? false : salesTeamLocked

  return (
    <FormSection
      label="Assignment"
      spacing={spacing}
      paddingBottom={4}
      maxWidth="660px"
    >
      {/* Info box */}
      {!!rule && !isOverriden && (
        <Grid item xs={12} sx={{ paddingTop: theme.spacing(1.6) }}>
          <Alert severity="info">
            <Typography
              variant="body2"
              letterSpacing="0.17px"
              color={theme.palette.text.secondary}
            >
              {invalidBranch ? (
                <>
                  <LeadAssignmentRuleInfoPopoverButton
                    label={rule.name}
                    rule={rule}
                  />{' '}
                  was not applied because you cannot assign this lead to a
                  different branch. Please select a Sales Team and Owner.
                </>
              ) : (
                <>
                  One or more values entered has triggered an automated lead
                  assignment rule which has preselected the ownership of this
                  lead.{' '}
                  <LeadAssignmentRuleInfoPopoverButton
                    label="View details"
                    rule={rule}
                  />
                </>
              )}
            </Typography>
          </Alert>
        </Grid>
      )}
      {/* sales team */}
      <Grid item xs={12} sx={{ paddingTop: theme.spacing(2) }}>
        <SalesTeamSelect
          name="salesTeamId"
          label="Sales Team"
          optionList={sortedOptions}
          onChange={onChangeSalesTeam}
          disabled={salesTeamDisabled}
          errorText={selectedSalesTeamAccessText}
        />
      </Grid>
      {/* owner */}
      <Grid item xs={12}>
        <OwnerSelect
          salesTeamId={salesTeamId}
          disabled={ownerDisabled}
          entityType={ENTITY_TYPES.Lead}
          name="assigneeId"
          label="Owner"
          required
          errorText={positionBasedAccessText}
          testId="ownerSelectAddLeadInt"
          fieldProps={textFieldProps}
          onChange={onChangeOwner}
          defaultSetRequired={false}
          defaultValue={defaultOwnerId}
          ignoreDefault={ignoreDefault}
          assignmentRuleData={{
            rule,
            canOverride: canOverrideAutomatedAssignment,
          }}
        />
      </Grid>
      {/* sales funnel */}
      <Grid item xs={12}>
        <SalesFunnelSelect
          name="salesFunnelId"
          label="Sales Funnel"
          salesFunnels={salesFunnels}
          disabled={assignmentFieldsLocked || disableAssignmentRule}
          onChange={onChangeSalesFunnel}
        />
      </Grid>
      {/* lead status */}
      <Grid item xs={12}>
        <LeadStatusSelect
          name="salesFunnelStageId"
          label="Lead Status"
          disabled={assignmentFieldsLocked || disableAssignmentRule}
          onChange={onChangeOwnerShip}
          salesFunnelStages={salesFunnelStages}
          density={density}
        />
      </Grid>
      {leadStatus === leadStatusDisqualified && (
        <Grid item xs={12}>
          <Stack
            sx={{
              backgroundColor: theme.palette.background.default,
              p: 1,
              border: reasonIdError ? '1px solid' : 'none',
              borderColor: theme.palette.error.main,
            }}
          >
            <RadioGroup {...reasonField} onBlur={undefined}>
              <Typography
                variant="body1"
                sx={{
                  color: reasonIdError
                    ? theme.palette.error.main
                    : theme.palette.text.primary,
                }}
              >
                Disqualified reason*
              </Typography>
              {disqualifyReasons.map((reason) => (
                <FormControlLabel
                  key={reason.id}
                  control={<Radio size="medium" />}
                  label={reason.reason}
                  value={reason.id.toString()}
                  sx={{ marginLeft: 0, marginBottom: 0, width: 'fit-content' }}
                  data-test-id={`disqualify-reason-${reason.id}`}
                />
              ))}

              {allowManualDisqualifyReasons && (
                <FormControlLabel
                  value="0"
                  control={<Radio />}
                  label="Custom Reason"
                  sx={{ marginLeft: 0, marginBottom: 0, width: 'fit-content' }}
                  data-test-id="disqualify-reason-custom"
                />
              )}
            </RadioGroup>

            {allowManualDisqualifyReasons && (
              <Box ml={4} mt={1}>
                <TextField
                  {...customReasonField}
                  fullWidth
                  inputRef={customReasonRef}
                  onFocus={onFocusCustomReason}
                  inputProps={{ maxLength: 50 }}
                  label={reasonId == '0' ? 'Custom Reason *' : 'Custom Reason'}
                  helperText={customReasonErrorTxt || customReasonHelperText}
                  error={customReasonError}
                  onBlur={undefined}
                  data-test-id="disqualify-reason-custom-input"
                />
              </Box>
            )}

            <FormHelperText error={reasonIdError}>
              {reasonIdErrorTxt}
            </FormHelperText>
          </Stack>
        </Grid>
      )}
    </FormSection>
  )
}

export default React.memo(Assignment)
